import { useCallback, useEffect, useState } from 'react';

const FOREVER = 'forever';

type CloseOptions = { forever?: boolean; skipLocalStorage?: boolean };

type UseDismissibleDialogParams = {
	storageKey: string;
	hiddenSeconds: number;
	skipInitializer?: boolean;
};

export const isDialogDismissed = (storageKey: string): boolean => {
	if (typeof localStorage === 'undefined') return false;

	const raw = localStorage.getItem(storageKey);
	if (!raw) return false;
	if (raw === FOREVER) return true;

	const until = Number(raw);
	if (Number.isNaN(until)) {
		localStorage.removeItem(storageKey);
		return false;
	}
	if (Date.now() < until) return true;

	localStorage.removeItem(storageKey);
	return false;
};

export const useDismissibleDialog = ({
	storageKey,
	hiddenSeconds,
	skipInitializer = false,
}: UseDismissibleDialogParams) => {
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (typeof globalThis === 'undefined' || skipInitializer) return;
		setOpen(!isDialogDismissed(storageKey));
	}, [storageKey, skipInitializer]);

	const openDialog = useCallback(() => {
		if (!isDialogDismissed(storageKey)) {
			setOpen(true);
		}
	}, [storageKey]);

	const closeForeverDialog = useCallback(() => {
		setOpen(false);
		if (typeof globalThis === 'undefined') return;
		localStorage.setItem(storageKey, FOREVER);
	}, [storageKey]);

	const closeSkippedDialog = useCallback(() => {
		setOpen(false);
		if (typeof globalThis === 'undefined') return;
		localStorage.setItem(storageKey, String(Date.now() + hiddenSeconds * 1000));
	}, [storageKey, hiddenSeconds]);

	const closeDirectDialog = useCallback(() => {
		setOpen(false);
	}, []);

	const closeDialog = useCallback(
		(options?: CloseOptions) => {
			if (options?.forever) {
				closeForeverDialog();
				return;
			}

			closeSkippedDialog();
		},
		[closeForeverDialog, closeSkippedDialog]
	);

	const cleanLocalStorage = useCallback(() => {
		if (typeof globalThis === 'undefined') return;
		localStorage.removeItem(storageKey);
	}, [storageKey]);

	return {
		open,
		openDialog,
		closeDialog,
		closeSkippedDialog,
		closeForeverDialog,
		closeDirectDialog,
		cleanLocalStorage,
	};
};
